import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CoverageCategory } from '@/services/types/coverage-types';

interface CategoryBreakdownProps {
  categoryCounts: Record<string, number>;
}

const categoryConfig = {
  [CoverageCategory.FIRST_PARTY]: {
    label: 'First Party',
    color: 'bg-blue-100 text-blue-800',
    description: "Client's own auto coverage",
  },
  [CoverageCategory.FIRST_PARTY_HEALTH]: {
    label: 'First Party Health',
    color: 'bg-green-100 text-green-800',
    description: "Client's health insurance",
  },
  [CoverageCategory.THIRD_PARTY]: {
    label: 'Third Party',
    color: 'bg-purple-100 text-purple-800',
    description: "At-fault party's coverage",
  },
  [CoverageCategory.NOT_LIABLE_INVOLVED_PARTY]: {
    label: 'Not-Liable Involved Party',
    color: 'bg-amber-100 text-amber-800',
    description: 'Involved but non-fault party coverage',
  },
  [CoverageCategory.UNCLEAR]: {
    label: 'Unclear',
    color: 'bg-gray-100 text-gray-800',
    description: 'Coverage needs classification',
  },
  UNKNOWN: {
    label: 'Unknown',
    color: 'bg-gray-100 text-gray-800',
    description: 'Unclassified coverage',
  },
};

export const CategoryBreakdown = ({
  categoryCounts,
}: CategoryBreakdownProps) => {
  const totalCount = Object.values(categoryCounts).reduce(
    (sum, count) => sum + count,
    0,
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Coverage Categories</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {Object.entries(categoryCounts)
            .sort(([, a], [, b]) => b - a) // Sort by count descending
            .map(([category, count]) => {
              const percentage =
                totalCount > 0 ? (count / totalCount) * 100 : 0;
              const config =
                categoryConfig[category as keyof typeof categoryConfig] ||
                categoryConfig.UNKNOWN;

              return (
                <div
                  key={category}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <Badge variant="secondary" className={config.color}>
                      {config.label}
                    </Badge>
                    <div>
                      <p className="text-sm font-medium">{count} mentions</p>
                      <p className="text-xs text-gray-500">
                        {config.description}
                      </p>
                    </div>
                  </div>
                  <span className="text-sm font-semibold">
                    {percentage.toFixed(1)}%
                  </span>
                </div>
              );
            })}
        </div>
      </CardContent>
    </Card>
  );
};
